import Box from "@mui/material/Box";
import useTheme from "@mui/material/styles/useTheme";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLazySvgImport } from "@/hooks/lazy-svg";
import i18n from "@/i18n";

interface TellTaleProps {
  iso: string;
  status: number; // 0 = off / 1 = red / 2 = yellow / 3 = information / 4 = reserved / 5 = reserved / 6 = reserved / 7 = not available
  name: string;
  date: Date | undefined;
  hideOldData: boolean;
  /** Fixed width in pixels, for a row that wraps; an eighteenth of the row where left out. */
  size?: number;
}

// TellTale \u2013 {name} (0 = off / 1 = red / 2 = yellow / 3 = information / 4 = reserved / 5 = reserved / 6 = reserved / 7 = not available)
const extractName = (input: string) => input.split("\u2013")[1]?.split("(")[0].trim() || null;

const TellTale: React.FC<TellTaleProps> = ({ iso, name, status, date, hideOldData, size }) => {
  const theme = useTheme();

  const { t } = useTranslation();
  const colorMap: Record<number, string> = {
    0: "white",
    1: theme.palette.error.main,
    2: theme.palette.warning.main,
    3: theme.palette.primary.main,
    4: theme.palette.grey[400],
    5: theme.palette.grey[400],
    6: theme.palette.grey[400],
    7: theme.palette.grey[400],
  };
  const statusMap: Record<number, string> = {
    0: t("faultOverview.off"),
    1: t("faultOverview.red"),
    2: t("faultOverview.yellow"),
    3: t("faultOverview.information2"),
    4: t("faultOverview.reserved"),
    5: t("faultOverview.reserved"),
    6: t("faultOverview.reserved"),
    7: t("faultOverview.notAvailable"),
  };

  const displayName = extractName(name);

  const [timeDifference, setTimeDifference] = useState("");
  const [isOld, setIsOld] = useState(false);
  useEffect(() => {
    const calculateTimeDifference = () => {
      const now = new Date();
      const diffInMilliseconds = now.getTime() - (date?.getTime() ?? 0);

      const minutes = Math.floor(diffInMilliseconds / (1000 * 60));
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      setTimeDifference(
        (days > 0 ? days + i18n.t("abrevtime.days") + " " : "") +
          (hours % 24 > 0 ? (hours % 24) + i18n.t("abrevtime.hours") + " " : "") +
          (minutes % 60) +
          i18n.t("abrevtime.minutes"),
      );
      setIsOld(days > 0);
    };

    calculateTimeDifference();
  }, [date]);

  const { loading, error, Svg } = useLazySvgImport(
    "ISO_7000_-_Ref-No_" + (iso === "-1" ? "NOICON" : iso.toString()),
  );

  if (isOld && hideOldData) {
    status = 7;
  }

  if (loading || !Svg) return <></>;
  if (error) {
    console.error(error);
    return <></>;
  }

  const color = colorMap[status ?? 7];
  const statusString = statusMap[status ?? 7];
  return (
    <Tooltip
      title={
        `${displayName} ${statusString}` +
        (date && status !== 7 ? " " + t("deviceStatus.since") + " " + timeDifference : "")
      }
    >
      <Box sx={{ position: "relative", width: size ?? "calc(100% / 18)", flexShrink: 0 }}>
        <Box
          sx={{
            width: "100%",
            height: "auto",
            border: "1px solid black",
            borderRadius: "50%",
            overflow: "hidden",
            backgroundColor: color || "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            src={Svg}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
        {/*hideOldData && status !== 7 && isOld && (
          <AccessTimeIcon
            sx={{
              position: "absolute",
              bottom: "-4px",
              right: "-4px",
              fontSize: "15px",
            }}
          />
        )*/}
      </Box>
    </Tooltip>
  );
};

export default TellTale;
